import {Observable, Subject} from 'rxjs';
import {SwitchState} from '../../switch';

export interface TableColumn<TKey extends string = any> {
    key: TKey;
    label: string;
    isRequired?: boolean;
    isHiddenByDefault?: boolean;
    isHidden?: boolean;
    width?: number | string;
    groupId?: any;
    sizeGroupId?: any;
}

export interface TableColumnConfig<TKey extends string = any> {
    key: TKey;
    width?: number | string;
}

export type TableColumnsDefMap<TKey extends string = string, TValue extends TableColumn = TableColumn> = {
    [key in TKey]: TValue;
};

export enum TableColumnsSizingMode {
    MANUAL = 'MANUAL',
    AUTO   = 'AUTO'
}

export interface TableColumnsSizingStep<GROUP = any> {
    sizeGroupId: GROUP;
    tableSizeDetectionClasses?: string;
    getGroupSize: (args: {
        fitContentWidth: number;
        restWidth: number;
        groupColumnCount: number;
        previousGroupWidth: number;
    }) => number;
}

export interface Table<ColumnKey extends string = any, Row = any> {
    columnsDict: TableColumnsDefMap<ColumnKey, TableColumn>;
    columnsConfig: TableColumnConfig[];
    columnsSizingSteps?: TableColumnsSizingStep[];
    autoResizeColumns$?: Observable<any>;
    rows: Row[];
    emptyRow: Row;
    disableEmptyRows?: boolean;
    columnsUpdate$: Subject<TableColumnConfig[]>;
    columnsSizingMode?: TableColumnsSizingMode;
    rowTrackFn?: <U extends Row>(index: number, row: Row & U) => any;
}

export const TABLE_COLUMN_SIZING_FN = {
    fitContent: (padding: number = 0) => ({fitContentWidth, groupColumnCount}: {
        fitContentWidth: number;
        groupColumnCount: number;
    }) =>
        (Math.ceil(fitContentWidth) + padding * 2) * groupColumnCount,
    fixedWidth: (width: number) => ({groupColumnCount}: {
        groupColumnCount: number;
    }) => width * groupColumnCount,
    stretch: () => ({restWidth}: {
        restWidth: number;
    }) => restWidth,
    lazyFitContent: (padding: number = 0) => ({fitContentWidth, restWidth, groupColumnCount, previousGroupWidth}: {
        fitContentWidth: number;
        restWidth: number;
        groupColumnCount: number;
        previousGroupWidth: number;
    }) => {
        const TABLE_COLUMN_RESIZING_THRESHOLD = 15;
        const fitContent = (Math.ceil(fitContentWidth) + padding * 2) * groupColumnCount;
        const groupWidth = Math.abs(fitContent - previousGroupWidth) > TABLE_COLUMN_RESIZING_THRESHOLD
            ? fitContent
            : previousGroupWidth;

        return Math.min(groupWidth, restWidth);
    },
    fitContentLazyShrink: (padding: number = 0) => (
        {fitContentWidth, restWidth, groupColumnCount, previousGroupWidth}: {
            fitContentWidth: number;
            restWidth: number;
            groupColumnCount: number;
            previousGroupWidth: number;
        }
    ) => {
        const TABLE_COLUMN_RESIZING_THRESHOLD = 15;
        const fitContent = (Math.ceil(fitContentWidth) + padding * 2) * groupColumnCount;
        const diff = previousGroupWidth - fitContent;
        const groupWidth = diff < 0 || diff > TABLE_COLUMN_RESIZING_THRESHOLD
            ? fitContent
            : previousGroupWidth;

        return Math.min(groupWidth, restWidth);
    }
};

export const TABLE_COLUMNS_RESIZING_MODE_SWITCH_CONFIG: SwitchState[] = [
    {
        label: 'components.table.columns_resizing_manual',
        value: TableColumnsSizingMode.MANUAL
    },
    {
        label: 'components.table.columns_resizing_auto',
        value: TableColumnsSizingMode.AUTO
    }
];
